import './OptionPanel.css';
import { CAR_MODELS, hasInteriorView } from '../data/carModels';
import { studioOptions } from '../data/studioOptions';
import { weatherOptions } from '../data/weatherOptions';
import { drivingCourseOptions, type DrivingCourseOption } from '../data/courseOptions';
import { timeOfDayOptions, type TimeOfDayOption } from '../data/timeOfDayOptions';
import { drivingCameraOptions, type DrivingCameraOption, type DrivingCameraMode } from '../data/drivingCameraOptions';
import { drivingChallengeOptions, type DrivingChallengeId, type DrivingChallengeOption } from '../data/challengeOptions';
import { graphicsQualityOptions, type GraphicsQualityOption } from '../data/graphicsQualityOptions';
import { findRegenOptionById } from '../data/regenOptions';
import { paintOptions } from '../data/paintOptions';
import { wheelOptions } from '../data/wheelOptions';
import { interiorOptions } from '../data/interiorOptions';
import { InteriorSwatchGroup } from './InteriorSwatchGroup';
import { ModelSelector } from './ModelSelector';
import { OptionSection } from './OptionSection';
import { PaintSwatchGroup } from './PaintSwatchGroup';
import { PriceSummary } from './PriceSummary';
import { StudioSwatchGroup } from './StudioSwatchGroup';
import { formatModelSubtitle } from '../model/modelLabels';
import { ThemeToggle } from './ThemeToggle';
import { ViewModeToggle } from './ViewModeToggle';
import { WeatherSelector } from './WeatherSelector';
import {
  ChallengeSelector,
  CourseSelector,
  DriverAssistToggles,
  DrivingCameraSelector,
  GraphicsQualitySelector,
  RegenModeSelector,
  TimeOfDaySelector,
  VehicleTuningControls,
  WeatherModeToggle,
} from './DrivingPreferenceControls';
import { WheelOptionGroup } from './WheelOptionGroup';
import type { ColorTheme } from '../hooks/useColorTheme';
import type { ViewMode } from '../three/cameraPoses';
import type { OptionSelectHandler } from '../hooks/useCarConfiguration';
import type { SelectedOptions } from '../types';
import type {
  DrivingAssistId,
  DrivingAssists,
  RegenMode,
  WeatherMode,
} from '../model/drivingPreferences';
import type { VehicleTuning, VehicleTuningField } from '../model/vehicleTuning';

export interface OptionPanelProps {
  theme?: ColorTheme;
  onThemeChange?: (theme: ColorTheme) => void;
  viewMode: string;
  onViewModeChange: (mode: ViewMode) => void;
  showViewModeToggle?: boolean;
  /** 주행 중 차량 사양과 보기 모드는 잠그고 배경·날씨·테마는 계속 허용합니다. */
  specLocked?: boolean;
  selectedOptions: SelectedOptions;
  requestedModelId?: string;
  onModelSelect: OptionSelectHandler;
  onStudioSelect: OptionSelectHandler;
  onWeatherSelect: OptionSelectHandler;
  drivingCourse: DrivingCourseOption;
  timeOfDay: TimeOfDayOption;
  drivingCamera: DrivingCameraOption;
  weatherMode: WeatherMode;
  drivingChallenge: DrivingChallengeOption;
  drivingAssists: DrivingAssists;
  regenMode: RegenMode;
  vehicleTuning: VehicleTuning;
  graphicsQuality: GraphicsQualityOption;
  onCourseSelect: (courseId: string) => void;
  onTimeOfDaySelect: (timeOfDayId: string) => void;
  onCameraSelect: (mode: DrivingCameraMode) => void;
  onWeatherModeToggle: () => void;
  onChallengeSelect: (id: DrivingChallengeId) => void;
  onRegenModeSelect: (mode: RegenMode) => void;
  onAssistToggle: (assist: DrivingAssistId) => void;
  onTuningSelect: (field: VehicleTuningField, value: string) => void;
  onTuningReset: () => void;
  onGraphicsQualitySelect: (id: string) => void;
  onPaintSelect: OptionSelectHandler;
  onWheelSelect: OptionSelectHandler;
  onInteriorSelect: OptionSelectHandler;
}

export function OptionPanel({
  theme,
  onThemeChange,
  viewMode,
  onViewModeChange,
  showViewModeToggle = true,
  specLocked = false,
  selectedOptions,
  requestedModelId,
  onModelSelect,
  onStudioSelect,
  onWeatherSelect,
  drivingCourse,
  timeOfDay,
  drivingCamera,
  weatherMode,
  drivingChallenge,
  drivingAssists,
  regenMode,
  vehicleTuning,
  graphicsQuality,
  onCourseSelect,
  onTimeOfDaySelect,
  onCameraSelect,
  onWeatherModeToggle,
  onChallengeSelect,
  onRegenModeSelect,
  onAssistToggle,
  onTuningSelect,
  onTuningReset,
  onGraphicsQualitySelect,
  onPaintSelect,
  onWheelSelect,
  onInteriorSelect,
}: OptionPanelProps) {
  const interiorAvailable = hasInteriorView(selectedOptions.model);

  return (
    <div className="option-panel">
      <header className="option-panel__intro">
        <div className="option-panel__intro-head">
          <span className="option-panel__eyebrow">BUILD YOUR CAR</span>
          <ThemeToggle theme={theme} onThemeChange={onThemeChange} />
        </div>
        <h2 className="option-panel__title">타운카 쇼룸</h2>
        <p className="option-panel__description">대여가능한 차량들을 만나보세요.</p>
      </header>

      {specLocked ? (
        <p className="option-panel__lock-note" role="status">
          주행 중에는 차량 사양만 잠깁니다. 배경, 날씨, 코스, 시간대와 주행 보조는 바꿀 수 있습니다.
        </p>
      ) : null}

      <OptionSection
        title="차량"
        collapsible={false}
        selectedOption={selectedOptions.model}
        selectionNote={formatModelSubtitle(selectedOptions.model)}
      >
        <ModelSelector
          models={CAR_MODELS}
          selectedModelId={requestedModelId ?? selectedOptions.model.id}
          onModelSelect={onModelSelect}
          disabled={specLocked}
        />
      </OptionSection>

      {showViewModeToggle ? (
        <ViewModeToggle
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          interiorAvailable={interiorAvailable}
          disabled={specLocked}
        />
      ) : null}

      <OptionSection
        title="배경"
        selectedOption={selectedOptions.studio}
        selectionNote={selectedOptions.studio.description}
      >
        <StudioSwatchGroup
          options={studioOptions}
          selectedOptionId={selectedOptions.studio.id}
          onOptionSelect={onStudioSelect}
        />
      </OptionSection>

      <OptionSection
        title="날씨"
        selectedOption={selectedOptions.weather}
        selectionNote={selectedOptions.weather.description}
      >
        <WeatherSelector
          options={weatherOptions}
          selectedOptionId={selectedOptions.weather.id}
          onOptionSelect={onWeatherSelect}
        />
        <WeatherModeToggle mode={weatherMode} onToggle={onWeatherModeToggle} />
      </OptionSection>

      <OptionSection
        title="주행 코스"
        collapsible={false}
        selectedOption={drivingCourse}
        selectionNote={drivingCourse.description}
      >
        <CourseSelector
          options={drivingCourseOptions}
          selectedId={drivingCourse.id}
          onSelect={onCourseSelect}
        />
      </OptionSection>

      <OptionSection
        title="주행 카메라"
        selectedOption={drivingCamera}
        selectionNote={drivingCamera.description}
      >
        <DrivingCameraSelector
          options={drivingCameraOptions}
          selectedId={drivingCamera.id}
          onSelect={onCameraSelect}
          /*
            실내 형상이 없는 모델은 운전석 시점의 눈 위치를 알 수 없습니다. 조용히 추격으로
            바뀌면 선택과 화면이 어긋나므로 고를 수 없게 둡니다.
          */
          disabledIds={interiorAvailable ? undefined : ['cockpit']}
          disabledNote="이 차량은 실내 형상이 없어 사용할 수 없습니다"
        />
      </OptionSection>

      <OptionSection
        title="시간대"
        selectedOption={timeOfDay}
        selectionNote={timeOfDay.description}
      >
        <TimeOfDaySelector
          options={timeOfDayOptions}
          selectedId={timeOfDay.id}
          onSelect={onTimeOfDaySelect}
        />
      </OptionSection>

      <OptionSection
        title="주행 보조"
        selectedOption={{ name: 'ABS · TCS · ESC' }}
        selectionNote="개별 활성화"
      >
        <DriverAssistToggles assists={drivingAssists} onToggle={onAssistToggle} />
      </OptionSection>

      {selectedOptions.model.powertrain === 'ev' ? (
        <OptionSection
          title="회생 제동"
          selectedOption={findRegenOptionById(regenMode)}
          selectionNote={findRegenOptionById(regenMode).description}
        >
          <RegenModeSelector
            powertrain={selectedOptions.model.powertrain}
            selectedId={regenMode}
            onSelect={onRegenModeSelect}
          />
        </OptionSection>
      ) : null}

      <OptionSection
        title="차량 튜닝"
        selectedOption={{ name: '드라이빙 셋업' }}
        selectionNote="타이어 · 서스펜션 · 기어비 · 브레이크"
      >
        <VehicleTuningControls
          tuning={vehicleTuning}
          onSelect={onTuningSelect}
          onReset={onTuningReset}
        />
      </OptionSection>

      <OptionSection
        title="주행 챌린지"
        selectedOption={drivingChallenge}
        selectionNote={drivingChallenge.description}
      >
        <ChallengeSelector
          options={drivingChallengeOptions}
          selectedId={drivingChallenge.id}
          onSelect={onChallengeSelect}
        />
      </OptionSection>

      <OptionSection
        title="그래픽 품질"
        selectedOption={graphicsQuality}
        selectionNote={graphicsQuality.description}
      >
        <GraphicsQualitySelector
          options={graphicsQualityOptions}
          selectedId={graphicsQuality.id}
          onSelect={onGraphicsQualitySelect}
        />
      </OptionSection>

      <OptionSection title="외장 색상" selectedOption={selectedOptions.paint}>
        <PaintSwatchGroup
          options={paintOptions}
          selectedOptionId={selectedOptions.paint.id}
          onOptionSelect={onPaintSelect}
          disabled={specLocked}
        />
      </OptionSection>

      <OptionSection title="휠" selectedOption={selectedOptions.wheel}>
        <WheelOptionGroup
          options={wheelOptions}
          selectedOptionId={selectedOptions.wheel.id}
          onOptionSelect={onWheelSelect}
          disabled={specLocked}
        />
      </OptionSection>

      {interiorAvailable ? (
        <OptionSection title="내장 색상" selectedOption={selectedOptions.interior}>
          <InteriorSwatchGroup
            options={interiorOptions}
            selectedOptionId={selectedOptions.interior.id}
            onOptionSelect={onInteriorSelect}
            disabled={specLocked}
          />
        </OptionSection>
      ) : null}

      <PriceSummary selectedOptions={selectedOptions} />
    </div>
  );
}
